import { useState, useContext } from 'react';
import { Row, Col, Card, Form, Select, Input, Switch, Tabs, Alert, Button } from 'antd';
import { getLandingUrlFromProps } from '$components/pages/util/domain';
import { AppContext } from '$util/AppContext';
import widgetList from '../../../server/data/Widgets';
import { StyledIframe, Container, PreviewButtons } from './styled';

export default function WidgetEditor() {
  const [widget, setWidget] = useState(null);
  const [previewSize, setPreviewSize] = useState(24);
  const [settings, updateSettings] = useState({});
  const { location } = useContext(AppContext);
  const landingUrl = getLandingUrlFromProps(location);

  const { TabPane } = Tabs;
  const { Option } = Select;
  const { TextArea } = Input;

  const editableWidgets = widgetList.filter((w) => w.data.editable);

  const selectWidget = (w) => setWidget(w);

  const getEmbedURL = () => {
    const base = landingUrl;
    const parsedWidget = JSON.parse(widget);
    const settingsString = Object.keys(settings)
      .map((key) => `${key}=${settings[key]}`)
      .join('&');
    const slug = parsedWidget ? parsedWidget.slug : '';
    return `${base}/${slug}?${settingsString}`;
  };

  const setFieldValue = (fieldId, value) =>
    updateSettings({ ...settings, [fieldId]: encodeURIComponent(value).replace(/'/g, '%27') });

  const renderField = (field) => {
    const { id, type, label } = field;
    let formField;
    switch (type) {
      case 'boolean':
        formField = <Switch onChange={(value) => setFieldValue(id, value)} />;
        break;
      case 'textarea':
        formField = <TextArea onChange={(event) => setFieldValue(id, event.target.value)} />;
        break;
      default:
        formField = <Input onChange={(event) => setFieldValue(id, event.target.value)} />;
        break;
    }
    return (
      <Form.Item name={id} label={label} key={id}>
        {formField}
      </Form.Item>
    );
  };

  const embedCode = () => `
    <iframe src="${getEmbedURL()}" sandbox="allow-same-origin allow-scripts allow-top-navigation allow-forms allow-popups" allow="autoplay" onload="setWidgetHeight(this)">
      Your browser does not support iFrames.
    </iframe>`;

  const howToUseText = () => (
    <div>
      <ol>
        <li>Select a widget from the dropdown on the left</li>
        <li>
          Set any text, images and other options are per your use case (basic{' '}
          <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank" rel="noopener noreferrer">
            markdown
          </a>{' '}
          is supported)
        </li>
        <li>
          Click the &#39;Preview&#39; tab and ensure your widget displays correctly in the different device
          sizes
        </li>
        <li>Copy the code below and insert it anywhere HTML is accepted</li>
      </ol>
      {widget && <TextArea readOnly value={embedCode()} autoSize />}
    </div>
  );

  const editableFields = widget ? JSON.parse(widget).data.editable : [];
  return (
    <Container>
      <Tabs defaultActiveKey="settings" animated={false}>
        <TabPane tab="Widget Settings" key="settings">
          <Row gutter={20}>
            <Col md={12}>
              <Card>
                <Form>
                  <Form.Item label="Select a widget">
                    <Select
                      onSelect={(w) => selectWidget(w)}
                      placeholder="Select a widget"
                      optionFilterProp="children"
                    >
                      {editableWidgets.map((w) => (
                        <Option key={w.slug} value={JSON.stringify(w)}>
                          {w.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {widget && editableFields.map((field) => renderField(field))}
                </Form>
              </Card>
            </Col>
            <Col md={12}>
              <Alert message="How to use a Fixter Widget" description={howToUseText()} type="info" />
            </Col>
          </Row>
        </TabPane>
        <TabPane tab="Preview" disabled={!widget} key="preview">
          {widget && (
            <>
              <PreviewButtons>
                <Button onClick={() => setPreviewSize(6)}>Mobile</Button>
                <Button onClick={() => setPreviewSize(12)}>Tablet</Button>
                <Button onClick={() => setPreviewSize(24)}>Desktop</Button>
              </PreviewButtons>
              <Row>
                <Col md={previewSize}>
                  <StyledIframe title="preview" src={getEmbedURL()}>
                    Your browser does not support iFrames.
                  </StyledIframe>
                </Col>
              </Row>
            </>
          )}
        </TabPane>
      </Tabs>
    </Container>
  );
}
